import React, { useState, createContext, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useFloating, useDismiss, offset, flip, size, autoUpdate, useTransitionStyles } from '@floating-ui/react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { ActionListBody } from './ActionListBody';
import { ActionListTrigger } from './ActionListTrigger';
import { PLACEMENT_MAPPING } from '../Utils/constant';
import useIsMobile from '../Utils/customHooks/useIsMobile';
import moduleStyles from '@dtsl/css-action-list/dist/index.module.css';
import MoreVertical from '@dtsl/icons/dist/icons/react/MoreVertical';
export const ActionListContext = /*#__PURE__*/createContext();

// eslint-disable-next-line react/display-name
const ActionList = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    className,
    placement,
    autoPlacement,
    options,
    triggerButtonMetaData,
    styles,
    dataTestId,
    defaultOpen
  } = props;
  const {
    isMobile
  } = useIsMobile(640);
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [focused, setFocused] = useState(null);
  const {
    refs,
    context,
    floatingStyles
  } = useFloating({
    placement: PLACEMENT_MAPPING[placement] || 'bottom-start',
    open: isOpen,
    onOpenChange: () => {
      setIsOpen(isOpen);
    },
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip({
      padding: 10
    }), size({
      apply(_ref) {
        let {
          rects,
          elements,
          availableHeight
        } = _ref;
        Object.assign(elements.floating.style, {
          maxHeight: `${availableHeight}px`,
          minWidth: `${rects.reference.width}px`,
          zIndex: `calc(var(--sib-z-index_modal, 1000) + 1)`
        });
      },
      padding: 10
    })]
  });
  // eslint-disable-next-line no-unused-vars
  const dismiss = useDismiss(context);
  const actionListRef = useRef();
  useEffect(() => {
    setIsOpen(defaultOpen);
  }, [defaultOpen]);
  useImperativeHandle(ref, () => ({
    toggleMenu: setIsOpen
  }));
  const handleDocumentClick = event => {
    if (actionListRef.current) {
      if (!actionListRef.current.contains(event.target) || event.target.classList.contains(styles['sib-action-list__body'])) {
        setIsOpen(false);
      }
    }
  };
  useEffect(() => {
    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [isOpen]);
  const actionListWrapperClasses = classNames({
    [moduleStyles['sib-action-list']]: true,
    [className]: !!className
  });
  const widthStylesForMobile = isMobile ? {
    width: 'calc(100% - 32px)'
  } : {};
  const {
    isMounted,
    styles: animationStyles
  } = useTransitionStyles(context, {
    duration: 300,
    initial: {
      opacity: 0,
      transform: isMobile ? 'none' : 'scale(0.8)'
    },
    open: {
      opacity: 1,
      transform: isMobile ? 'none' : 'scale(1)'
    }
  });
  return /*#__PURE__*/React.createElement(ActionListContext.Provider, {
    value: {
      isOpen,
      setIsOpen,
      focused,
      setFocused,
      className,
      autoPlacement,
      isMobile
    }
  }, /*#__PURE__*/React.createElement("div", {
    ref: actionListRef,
    className: actionListWrapperClasses
  }, /*#__PURE__*/React.createElement(ActionListTrigger, {
    refs: refs,
    triggerButtonMetaData: triggerButtonMetaData
  }), /*#__PURE__*/React.createElement(ActionListBody, {
    styles: {
      ...styles,
      ...widthStylesForMobile
    },
    dataTestId: dataTestId,
    options: options,
    refs: refs,
    floatingStyles: floatingStyles,
    context: context,
    isMounted: isMounted,
    animationStyles: animationStyles
  })), isOpen && isMobile && /*#__PURE__*/React.createElement("div", {
    className: moduleStyles['sib-action-list-backdrop'],
    style: {
      animation: `${isOpen ? 'var(--sib-transition_backdrop-open-action-list backdrop-open-action-list 300ms ease-in-out)' : 'var(--sib-transition_backdrop-close-action-list, backdrop-close-action-list 300ms ease-in-out)'}`
    }
  }));
});
ActionList.defaultProps = {
  className: '',
  autoPlacement: false,
  placement: 'bottomRight',
  triggerButtonMetaData: {
    iconAlone: true,
    variant: 'tertiary',
    icon: /*#__PURE__*/React.createElement(MoreVertical, null),
    ariaLabel: 'action-list',
    onClick: () => {}
  },
  options: [],
  dataTestId: '',
  styles: {},
  defaultOpen: false
};
ActionList.propTypes = {
  className: PropTypes.string,
  autoPlacement: PropTypes.bool,
  placement: PropTypes.oneOf(['topLeft', 'topRight', 'bottomLeft', 'bottomRight']),
  triggerButtonMetaData: PropTypes.object,
  options: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      subText: PropTypes.string,
      icon: PropTypes.func,
      tag: PropTypes.func,
      isDestructive: PropTypes.bool,
      disabled: PropTypes.bool,
      onSelect: PropTypes.func
    }))
  })),
  dataTestId: PropTypes.string,
  styles: PropTypes.object,
  defaultOpen: PropTypes.bool
};
export default ActionList;